import React, { useState } from 'react';
import { ExpandableCard } from '@/components/shared/ExpandableCard';
import { Button } from '@/components/shared';

interface ProfessionalPathContentProps {
    onGetStarted: () => void;
    className?: string;
}

export const ProfessionalPathContent: React.FC<ProfessionalPathContentProps> = ({
    onGetStarted,
    className = ''
}) => {
    // State to track which service cards are expanded
    const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());

    // Function to toggle expansion of a service card
    const toggleServiceExpansion = (serviceId: string) => {
        const newExpanded = new Set(expandedServices);
        if (newExpanded.has(serviceId)) {
            newExpanded.delete(serviceId);
        } else {
            newExpanded.add(serviceId);
        }
        setExpandedServices(newExpanded);
    };
    const whyProfessionalsChooseBiBi = [
        {
            title: 'Steady Client Stream',
            description: 'Get access to a consistent stream of seniors needing your reliable, professional services.',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            )
        },
        {
            title: 'Your Schedule, Your Control',
            description: 'Work on your own schedule. Accept the jobs that fit your availability and expertise. No minimums required.',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
            )
        },
        {
            title: 'Meaningful Impact',
            description: 'Help seniors maintain independence while building a sustainable and respected practice in your community.',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            )
        }
    ];

    const services = [
        {
            id: 'transportation',
            title: 'Transportation & Mobility Support',
            description: 'Driving and mobility assistance to help seniors get where they need to go safely',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
            ),
            examples: ['Professional driving services', 'Medical appointment assistance', 'Mobility equipment setup', 'Errand & shopping assistance']
        },
        {
            id: 'home-maintenance',
            title: 'Home Maintenance & Safety',
            description: 'Expert repairs and modifications to keep seniors safe and comfortable in their homes',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            ),
            examples: ['Skilled repairs', 'Safety modifications', 'Seasonal maintenance', 'Accessibility improvements']
        },
        {
            id: 'specialized-services',
            title: 'Specialized Professional Services',
            description: 'Quality specialized services to help seniors maintain their homes and enjoy life',
            icon: (
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
            ),
            examples: ['Pet care', 'Yard maintenance', 'Cleaning services', 'Other skilled trade work']
        }
    ];

    return (
        <div className={className}>

            {/* Services You Can Offer Section */}
            <section className="pt-16 pb-0 bg-purple-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Services We're Developing
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Professional services that help seniors maintain independence in their homes
                        </p>
                    </div>

                    {/* Important Disclaimer */}
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-12 max-w-3xl mx-auto">
                        <div className="flex items-start">
                            <svg className="w-6 h-6 text-orange-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.73 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <div>
                                <h4 className="font-semibold text-orange-800 mb-2">Important: Non-Medical Services Only</h4>
                                <p className="text-orange-700 text-sm leading-relaxed">
                                    BiBi focuses on non-medical support services. We do not provide or coordinate medical care,
                                    nursing services, or other licensed healthcare services.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Service Cards Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-start">
                        {services.map((service) => (
                            <ExpandableCard
                                key={service.id}
                                title={service.title}
                                description={service.description}
                                icon={service.icon}
                                examples={service.examples}
                                colorScheme="purple"
                                isExpanded={expandedServices.has(service.id)}
                                onToggle={() => toggleServiceExpansion(service.id)}
                            />
                        ))}
                    </div>

                    <div className="text-center mt-8">
                        <p className="text-sm text-gray-500 italic">
                            *Note: Specific service offerings will be refined based on community needs and professional interest
                        </p>
                    </div>
                </div>
            </section>

            {/* Your Professional Journey Section */}
            <section className="pt-0 pb-8 bg-purple-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12 pt-8">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Your Professional Journey
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Join BiBi's professional network and grow your business while making a meaningful difference in seniors' lives
                        </p>
                    </div>

                    {/* Large Icon Grid with Minimal Text */}
                    <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto mb-12">
                        {whyProfessionalsChooseBiBi.map((benefit, index) => (
                            <div key={index} className="text-center">
                                {/* Large 80px Icon */}
                                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 hover:bg-purple-200 transition-colors duration-200">
                                    <div className="text-purple-600">
                                        {benefit.icon}
                                    </div>
                                </div>

                                {/* Title */}
                                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>

                                {/* Descriptive Text */}
                                <p className="text-gray-600 leading-relaxed">
                                    {benefit.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Business Control Section */}
                    <div className="mt-12 bg-white rounded-xl p-8 shadow-lg border border-gray-200 max-w-4xl mx-auto">
                        <h4 className="text-xl font-semibold text-gray-900 mb-6 text-center">You're in Control of Your Business</h4>
                        <div className="grid md:grid-cols-3 gap-6 mb-6">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600 mb-2">Keep 85% of Your Rate</div>
                                <p className="text-gray-600">Our transparent 15% platform fee helps us find you clients and manage payments</p>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600 mb-2">Flexible Rates</div>
                                <p className="text-gray-600">Set your own hourly rates within our fair market guidelines</p>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600 mb-2">Payment Processing</div>
                                <p className="text-gray-600">We handle all billing and secure payments</p>
                            </div>
                        </div>
                        <div className="mt-6 text-center text-sm text-gray-500">
                            No hidden fees • Full payment transparency
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-8 bg-purple-600 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                        Ready to Join Our Network?
                    </h2>
                    <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
                        Join BiBi's professional network and start helping seniors while growing your business
                    </p>
                    <Button
                        variant="custom"
                        size="lg"
                        onClick={onGetStarted}
                        className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors focus:outline-none focus:ring-4 focus:ring-white/50"
                    >
                        Join Early Access
                    </Button>
                    <p className="text-sm text-purple-100 mt-4">
                        Launch early 2026
                    </p>
                </div>
            </section>
        </div>
    );
};
